import { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { Label } from '@accure/ui';
import type { SelectOptionObject } from '@looker/components';

import { GetAllVehicles } from '@/api/fe-api/view/queries/get-all-vehicles';
import { FieldSelect } from '@/components/looker/field-select';
import { useMe } from '@/hooks/use-me';

export function VehicleSelector() {
  const history = useHistory();
  const vehicleIdRef = useRef<HTMLInputElement | null>(null);
  const submitRef = useRef<HTMLButtonElement | null>(null);
  const me = useMe();
  const [value, setValue] = useState<string | undefined>(undefined);
  const topLabel = me.config.sitesWithDifferentLevels
    ? 'Top Level ID'
    : me.config.top.labels.id;

  const { data: vehicles, isLoading } = GetAllVehicles.useQuery();

  const topOptions = useMemo<SelectOptionObject[]>(
    () =>
      vehicles
        ?.map(({ fullFrontendId }) => ({
          value: fullFrontendId,
        }))
        .sort((a, b) => a.value.localeCompare(b.value)) ?? [],
    [vehicles],
  );

  function onVehicleSearchSuccess(vehicleId: string) {
    setValue(vehicleId);
    history.push(generatePath('/vehicle/:id', { id: vehicleId }));
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        submitRef.current?.click();
        event.stopPropagation();
      }
    }

    // Copy the reference into a variable to avoid losing the reference during cleanup
    const vehicleId = vehicleIdRef.current;
    vehicleId?.addEventListener('keydown', handleKeyDown);

    return () => {
      vehicleId?.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="rounded border p-4">
      <h3 className="mt-2 px-10 text-xl font-medium">Vehicle Selector</h3>
      <div className="p-10">
        <Label htmlFor="vehicle-id" className="mb-2.5">
          Access vehicle details by entering an ID
        </Label>
        <div className="flex gap-2">
          <FieldSelect
            placeholder={topLabel}
            containerClassName="w-full"
            options={topOptions}
            isFilterable={true}
            value={value}
            isLoading={isLoading}
            onChange={(id: string) => onVehicleSearchSuccess(id)}
          />
        </div>
      </div>
    </div>
  );
}
